import type { Modification, ModificationGroupLimits, ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the effective ingredient ID for an add operation.
 *
 * When the included group is at its max capacity and a paired extras
 * ingredient exists, returns the extras ingredient ID so the addition
 * is routed to the paid tier. Otherwise, returns the original ID.
 */
export function resolveIngredientIdForAdd(params: ResolveParams): string {
  const {
    ingredientId,
    allModificationsMap,
    extrasModificationsMap,
    modificationLimits,
    activeModifications,
  } = params;

  const modification = allModificationsMap.get(ingredientId);

  if (!modification?.ingredientId) return ingredientId;

  const extrasModification = extrasModificationsMap.get(modification.ingredientId);

  if (!extrasModification?.ingredient) return ingredientId;

  const { modificationGroupName } = modification;
  const groupLimits = modificationLimits[modificationGroupName];

  if (!groupLimits) return ingredientId;

  const activeCount = activeModifications
    .filter((active) => active.modificationGroupName === modificationGroupName)
    .reduce((total, active) => total + active.quantity, 0);

  if (activeCount >= groupLimits.maxAggregateQuantity) return extrasModification.ingredient.id;

  return ingredientId;
}

/**
 * Resolves the effective ingredient ID for a remove operation.
 *
 * When a paired extras ingredient is actively selected, returns the extras
 * ingredient ID so the paid tier is removed first.
 * Otherwise, returns the original included ingredient ID.
 */
export function resolveIngredientIdForRemove(params: ResolveParams): string {
  const { ingredientId, allModificationsMap, extrasModificationsMap, activeModifications } = params;

  const modification = allModificationsMap.get(ingredientId);

  if (!modification?.ingredientId) return ingredientId;

  const extrasModification = extrasModificationsMap.get(modification.ingredientId);

  if (!extrasModification?.ingredient) return ingredientId;

  const extrasIngredientId = extrasModification.ingredient.id;

  const hasActiveExtras = activeModifications.some(
    (active) => active.ingredient?.id === extrasIngredientId,
  );

  if (hasActiveExtras) return extrasIngredientId;

  return ingredientId;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type ResolveParams = {
  ingredientId: string;
  allModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  modificationLimits: ModificationGroupLimits;
  activeModifications: Modification[];
};
